import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { AnimatePresence, motion } from "framer-motion";
import { Instagram, Mail, Menu, Phone, X } from "lucide-react";
import { AREAS, EMAIL, PHONE_DISPLAY, PHONE_TEL } from "@/data/content";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/locations", label: "Service Areas" },
  { to: "/contractors", label: "For Contractors" },
];

export default function SiteLayout() {
  const { pathname } = useLocation();
  const lenisRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    let rafId;
    const raf = (t) => { lenis.raf(t); rafId = requestAnimationFrame(raf); };
    rafId = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(rafId); lenis.destroy(); };
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    window.scrollTo(0, 0);
    lenisRef.current?.scrollTo(0, { immediate: true });
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || menuOpen;
  const showStickyCta = scrolled && pathname !== "/quote";

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#1C201D]">
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          solid ? "bg-[#FAF7F2]/90 backdrop-blur-md border-b border-[#E0D8CC]/70 shadow-[0_4px_20px_rgba(28,32,29,0.05)]" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8 py-4 sm:py-5">
          <Link to="/" data-testid="nav-logo" className="leading-none">
            <span className={`font-display text-2xl sm:text-[1.7rem] font-medium tracking-tight transition-colors duration-500 ${solid ? "text-[#1C201D]" : "text-[#FAF7F2]"}`}>
              Estate Outdoors
            </span>
            <span className={`block text-[0.6rem] font-semibold uppercase tracking-[0.4em] mt-1 transition-colors duration-500 ${solid ? "text-[#C5A059]" : "text-[#C5A059]"}`}>
              Calgary
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-9" aria-label="Primary">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-sm font-medium tracking-wide transition-colors duration-300 hover:text-[#C5A059] ${
                  solid ? "text-[#1C201D]" : "text-[#FAF7F2]"
                }`}
              >
                {item.label}
              </NavLink>
            ))}
            <Link to="/quote" data-testid="nav-quote-btn" className="btn-primary !px-6 !py-3">
              Request a Private Quote
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            className={`lg:hidden p-2 transition-colors ${solid ? "text-[#1C201D]" : "text-[#FAF7F2]"}`}
          >
            {menuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        <AnimatePresence>
          {menuOpen && (
            <motion.nav
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.35 }}
              className="lg:hidden overflow-hidden border-t border-[#E0D8CC]/70 bg-[#FAF7F2]"
              aria-label="Mobile"
            >
              <div className="px-6 py-6 flex flex-col gap-5">
                {NAV.map((item) => (
                  <NavLink key={item.to} to={item.to} data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`} className="font-display text-2xl text-[#1C201D]">
                    {item.label}
                  </NavLink>
                ))}
                <Link to="/quote" data-testid="mobile-nav-quote-btn" className="btn-primary mt-2 w-full">
                  Request a Private Quote
                </Link>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>

      <main>
        <Outlet />
      </main>

      <footer data-testid="site-footer" className="relative grain bg-[#1C201D] text-[#FAF7F2]">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-20 pb-28 md:pb-12">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-4">
              <p className="font-display text-3xl font-medium">Estate Outdoors</p>
              <p className="text-[0.6rem] font-semibold uppercase tracking-[0.4em] text-[#C5A059] mt-1">Calgary</p>
              <p className="mt-6 text-sm leading-relaxed text-[#B9BDB4] max-w-xs">
                A luxury landscape design-build studio for estate homes, new builds and acreage properties —
                designed and built by one dedicated local team.
              </p>
              <a href="#" data-testid="footer-instagram" aria-label="Instagram (coming soon)" className="mt-6 inline-flex items-center gap-2 text-sm text-[#B9BDB4] hover:text-[#C5A059] transition-colors">
                <Instagram size={16} /> Instagram — coming soon
              </a>
            </div>
            <div className="md:col-span-3">
              <p className="eyebrow mb-5">Explore</p>
              <ul className="space-y-3 text-sm text-[#B9BDB4]">
                <li><Link data-testid="footer-link-services" to="/services" className="hover:text-[#C5A059] transition-colors">Our Services</Link></li>
                <li><Link data-testid="footer-link-locations" to="/locations" className="hover:text-[#C5A059] transition-colors">Service Areas</Link></li>
                <li><Link data-testid="footer-link-luxury-landscaping" to="/luxury-landscaping-calgary" className="hover:text-[#C5A059] transition-colors">Luxury Landscaping Calgary</Link></li>
                <li><Link data-testid="footer-link-guide" to="/guide" className="hover:text-[#C5A059] transition-colors">Planning Guide</Link></li>
                <li><Link data-testid="footer-link-quote" to="/quote" className="hover:text-[#C5A059] transition-colors">Request a Quote</Link></li>
                <li><Link data-testid="footer-link-contractors" to="/contractors" className="hover:text-[#C5A059] transition-colors">Contractor Application</Link></li>
                <li><Link data-testid="footer-link-privacy" to="/privacy" className="hover:text-[#C5A059] transition-colors">Privacy Policy</Link></li>
                <li><Link data-testid="footer-link-admin" to="/admin" className="hover:text-[#C5A059] transition-colors">Team Login</Link></li>
              </ul>
            </div>
            <div className="md:col-span-3">
              <p className="eyebrow mb-5">Service Areas</p>
              <p className="text-sm leading-loose text-[#B9BDB4]">
                {AREAS.map((a) => a.name).join(" · ")}
              </p>
            </div>
            <div className="md:col-span-2">
              <p className="eyebrow mb-5">Contact</p>
              <a data-testid="footer-phone" href={PHONE_TEL} className="flex items-center gap-2 text-sm text-[#B9BDB4] hover:text-[#C5A059] transition-colors">
                <Phone size={15} /> {PHONE_DISPLAY}
              </a>
              <a data-testid="footer-email" href={`mailto:${EMAIL}`} className="mt-3 flex items-center gap-2 text-sm text-[#B9BDB4] hover:text-[#C5A059] transition-colors">
                <Mail size={15} /> {EMAIL}
              </a>
            </div>
          </div>
          <div className="mt-14 pt-8 border-t border-[#FAF7F2]/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#8A918C]">
            <p>© 2026 Estate Outdoors Calgary. All rights reserved.</p>
            <p>Luxury landscaping · Calgary, Springbank, Bearspaw & Rocky View County</p>
          </div>
        </div>
      </footer>

      <AnimatePresence>
        {showStickyCta && (
          <motion.div
            initial={{ y: 90, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 90, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-4 inset-x-4 z-40 md:hidden"
          >
            <Link
              to="/quote"
              data-testid="sticky-mobile-quote-btn"
              className="btn-primary w-full shadow-[0_10px_30px_rgba(28,32,29,0.3)]"
            >
              Request a Private Quote
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
